// M&A Phase 1 deal lens — contract types and closed enums.
//
// Charter: docs/charters/ma-pmi-product-charter.md §1 Phase 1. Property ledger:
// docs/research/ma-golden-worksheet-20260709.md, executable at
// packages/lib/src/ma-golden-worksheet.spec.ts. Both are law here.
//
// The composition itself lives in `ma-deal-lens.ts`; this file holds only the
// shapes and the closed enums every branch in that composer switches on, so no
// control flow anywhere in the lens matches on a free-form string.

import type { buildMaFusionDemoDryRun } from './ma-target-packs';
import type { MaTargetPack } from './ma-target-packs';
import type {
  SynergyCertaintyClass,
  SynergyLeverCommitment,
  SynergyValueType,
} from './ma-synergy-commitment';
import type { PoolFootprintAtom } from './ask-helix/contracts/governed-tool-output-envelope';

export const MA_DEAL_LENS_SCHEMA_VERSION = 'ma_deal_lens_v1' as const;

/**
 * The two governed certainty registers rendered side by side. Target-side facts
 * carry `MaTargetPackCertaintyClass`; synergy figures carry
 * `SynergyCertaintyClass`. They share one closed union so a bridge row can name
 * the class of whichever register it came from — they are never re-labelled.
 */
export const MA_DEAL_LENS_CERTAINTY_CLASSES = [
  'c0_observed',
  'public_filing',
  'ir_material',
  'c1_committed_lever',
  'c2_modelled_upside',
  'extrapolated_assumption',
  'c3_hypothesis',
  'blocked',
] as const;

export type MaDealLensCertaintyClass = (typeof MA_DEAL_LENS_CERTAINTY_CLASSES)[number];

/**
 * Evidence strength, strongest first. A figure derived from inputs of mixed
 * class takes the WEAKEST contributing class — a mixed number may never claim
 * the stronger register.
 */
export const CERTAINTY_STRENGTH_RANK: Readonly<Record<MaDealLensCertaintyClass, number>> = {
  c0_observed: 0,
  public_filing: 1,
  ir_material: 2,
  c1_committed_lever: 3,
  c2_modelled_upside: 4,
  extrapolated_assumption: 5,
  c3_hypothesis: 6,
  blocked: 7,
};

/** Facts at or above `ir_material` strength count as evidence in the verdict. */
export const EVIDENCE_GRADE_MAX_RANK = CERTAINTY_STRENGTH_RANK.ir_material;

export const MA_DEAL_LENS_UNITS = ['usd', 'headcount', 'date', 'text'] as const;
export type MaDealLensUnit = (typeof MA_DEAL_LENS_UNITS)[number];

export const MA_DEAL_LENS_PACK_FACT_KEYS = [
  'legal_name',
  'industry',
  'headquarters',
  'fiscal_period',
  'revenue_usd',
  'operating_income_usd',
  'sga_usd',
  'total_headcount',
] as const;

export const MA_DEAL_LENS_DEAL_FACT_KEYS = [
  'consideration_cash_usd',
  'consideration_stock_usd',
  'announce_date',
  'close_date',
] as const;

export type MaDealLensPackFactKey = (typeof MA_DEAL_LENS_PACK_FACT_KEYS)[number];
export type MaDealLensDealFactKey = (typeof MA_DEAL_LENS_DEAL_FACT_KEYS)[number];
export type MaDealLensTargetFactKey = MaDealLensPackFactKey | MaDealLensDealFactKey;

export const MA_DEAL_LENS_EV_BRIDGE_KEYS = [
  'target_standalone_operating_income',
  'gross_synergy_c0_observed',
  'gross_synergy_c1_committed_lever',
  'gross_synergy_c2_modelled_upside',
  'costs_to_achieve_one_time',
  'net_synergy_base',
  'combined_operating_income_delta',
] as const;

export type MaDealLensEvBridgeKey = (typeof MA_DEAL_LENS_EV_BRIDGE_KEYS)[number];

/**
 * Which certainty classes may contribute priced gross to the bridge, and under
 * which row. `c3_hypothesis` and `blocked` are never priced (charter §3.1), so
 * they map to `null` and render as gaps instead of dollars.
 */
export const GROSS_ROW_BY_CERTAINTY_CLASS: Readonly<
  Record<SynergyCertaintyClass, MaDealLensEvBridgeKey | null>
> = {
  c0_observed: 'gross_synergy_c0_observed',
  c1_committed_lever: 'gross_synergy_c1_committed_lever',
  c2_modelled_upside: 'gross_synergy_c2_modelled_upside',
  c3_hypothesis: null,
  blocked: null,
};

export const MA_DEAL_LENS_CASE_KEYS = ['conservative', 'base', 'stretch'] as const;
export type MaDealLensCaseKey = (typeof MA_DEAL_LENS_CASE_KEYS)[number];

export const MA_DEAL_LENS_GAP_KINDS = [
  'target_pack_data_gap',
  'lever_blocking_gap',
  'blocked_lever',
  'footprint_collision',
  'separation_receipt',
  'deal_fact_missing',
  'target_fact_missing',
  'rejected_commitment',
  'data_room_only',
] as const;

export type MaDealLensGapKind = (typeof MA_DEAL_LENS_GAP_KINDS)[number];

export const MA_DEAL_LENS_SEAT_BASIS =
  'Seats re-derive from the Fusion dry-run overlap families: acquirer seats are the ' +
  'distinct acquirer positions matched into the zone, target seats the distinct target ' +
  'positions matched, and overlap seats the smaller of the two — the most duplicate ' +
  'pairs the zone can contain. No seat count in this lens is typed as text.';

/** What no pre-signing filing can answer. Fixed list — the honesty map's floor. */
export const MA_DEAL_LENS_DATA_ROOM_UNLOCKS = [
  {
    gapCode: 'data_room_hris_census',
    label: 'Seat-level HRIS census for the target',
    unlockedBy: 'Data-room HRIS extract or Day-1 source-system access',
  },
  {
    gapCode: 'data_room_comp_bands',
    label: 'Target compensation bands and benefits load by role family',
    unlockedBy: 'Data-room payroll and benefits schedules',
  },
  {
    gapCode: 'data_room_customer_cohorts',
    label: 'Target customer cohorts, concentration, and contract terms',
    unlockedBy: 'Data-room customer contract and revenue-cohort schedules',
  },
  {
    gapCode: 'data_room_carrier_contracts',
    label: 'Target carrier and vendor contracts with rate and term detail',
    unlockedBy: 'Data-room procurement contract register',
  },
] as const;

export type MaDealLensTargetFact = Readonly<{
  key: MaDealLensTargetFactKey;
  label: string;
  value: string | number | null;
  unit: MaDealLensUnit;
  certaintyClass: MaDealLensCertaintyClass;
  sourceIds: readonly string[];
  notes: string | null;
}>;

export type MaDealLensEvBridgeRow = Readonly<{
  key: MaDealLensEvBridgeKey;
  label: string;
  valueUsd: number | null;
  certaintyClass: MaDealLensCertaintyClass;
  /** Bridge keys this row's arithmetic consumes. Empty when copied from an input. */
  derivedFrom: readonly MaDealLensEvBridgeKey[];
  /** Source ids when copied from an input. Empty when derived. */
  sourceIds: readonly string[];
}>;

export type MaDealLensCaseLever = Readonly<{
  synergyLeverId: string;
  certaintyClass: SynergyCertaintyClass;
  grossUsd: number;
  oneTimeCostUsd: number;
  netUsd: number;
}>;

export type MaDealLensGrossByCertaintyClass = Readonly<{
  certaintyClass: SynergyCertaintyClass;
  grossUsd: number;
  leverCount: number;
}>;

export type MaDealLensCase = Readonly<{
  caseKey: MaDealLensCaseKey;
  grossUsd: number;
  oneTimeCostUsd: number;
  netUsd: number;
  /** Both parts of any combined gross, always rendered alongside it. */
  grossByCertaintyClass: readonly MaDealLensGrossByCertaintyClass[];
  byLever: readonly MaDealLensCaseLever[];
}>;

export type MaDealLensCases = Readonly<Record<MaDealLensCaseKey, MaDealLensCase>>;

export type MaDealLensCandidate = Readonly<{
  rank: number;
  registerRecommendationRank: number;
  synergyLeverId: string;
  title: string;
  certaintyClass: SynergyCertaintyClass;
  valueType: SynergyValueType;
  ownerLabel: string;
  workstreamId: string;
  conservativeRunRateUsd: number;
  baseRunRateUsd: number;
  stretchRunRateUsd: number;
  oneTimeCostUsd: number;
  baseNetRunRateUsd: number;
  blockingGapCodes: readonly string[];
  footprintAtoms: readonly PoolFootprintAtom[];
  /** False when the lever contributes no dollars to any case (charter §3.1). */
  countsTowardCases: boolean;
}>;

export type MaDealLensOverlapZone = Readonly<{
  zone: string;
  acquirerSeats: number;
  targetSeats: number;
  overlapSeats: number;
  sourceClass: MaDealLensCertaintyClass;
  seatBasis: string;
}>;

export type MaDealLensGap = Readonly<{
  gapKind: MaDealLensGapKind;
  gapCode: string;
  label: string;
  unlockedBy: string;
  /** Lever this gap blocks, when the gap is lever-scoped. */
  synergyLeverId: string | null;
}>;

export type MaDealLensProvenance = Readonly<{
  packId: string;
  dealId: string;
  sourceIds: readonly string[];
  asOf: string;
}>;

export type MaDealLensDto = Readonly<{
  schemaVersion: typeof MA_DEAL_LENS_SCHEMA_VERSION;
  verdict: string;
  targetFacts: readonly MaDealLensTargetFact[];
  evBridge: readonly MaDealLensEvBridgeRow[];
  cases: MaDealLensCases;
  candidates: readonly MaDealLensCandidate[];
  overlapZones: readonly MaDealLensOverlapZone[];
  gaps: readonly MaDealLensGap[];
  provenance: MaDealLensProvenance;
}>;

export type MaDealLensDealFactInput = Readonly<{
  key: MaDealLensDealFactKey;
  label: string;
  value: string | number | null;
  unit: MaDealLensUnit;
  certaintyClass: MaDealLensCertaintyClass;
  sourceIds: readonly string[];
  notes?: string | null;
}>;

export type BuildMaDealLensInput = Readonly<{
  dealId: string;
  asOf: string;
  dealThesis: string;
  targetPack: MaTargetPack;
  acquirer: Parameters<typeof buildMaFusionDemoDryRun>[0]['acquirer'];
  commitments: readonly SynergyLeverCommitment[];
  /**
   * Deal consideration and dates. Each fact is an EXPLICIT input carrying its own
   * certainty class and source ids — the pack holds no consideration field and this
   * composer never invents one. A missing fact becomes a named gap, never a zero.
   */
  dealFacts: readonly MaDealLensDealFactInput[];
}>;
